import { Autocomplete, Box, Card, CardHeader, Dialog, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import CachedIcon from '@mui/icons-material/Cached'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { FilterAltOff, Search } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { CountryAvatar } from 'src/components/icons/CountryAvatar'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, PaisLogo, PaisLogoFields } from 'src/types'
import { ICON_MAS } from '../../../../sistema/components/SharedIcons'
import { WrapperTable } from '../components'
import { HttpInstrumentoLCSLHPTI } from './api'
import EditInstrumentoLCSLHPTIForm from './EditInstrumentoLCSLHPTIForm'
import { InstrumentoLCSLHPTIDTO, InstrumentoLCSLHPTIFields } from './types'

function TableInstrumentosLCSLHPTI() {
    const { askConfirmation } = useConfirmationDialog()
    const { paisLogos, loading: loadingPaises } = usePaisLogo()
    const [selectedPais, setSelectedPais] = useState<PaisLogo | null>(null)
    const [instrumentos, setInstrumentos] = useState<InstrumentoLCSLHPTIDTO[]>([])
    const [selectedEdit, setSelectedEdit] = useState<InstrumentoLCSLHPTIDTO>()
    const [showDialog, setShowDialog] = useState(false)
    const [filter, setFilter] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!selectedPais && paisLogos.length === 1) {
            setSelectedPais(paisLogos[0])
        }
    }, [paisLogos, selectedPais])

    const fetchInstrumentos = () => {
        if (!selectedPais) {
            setInstrumentos([])
        } else {
            setLoading(true)
            HttpInstrumentoLCSLHPTI.getPorPais(selectedPais[PaisLogoFields.CodPais])
                .then(setInstrumentos)
                .finally(() => setLoading(false))
        }
    }

    useEffect(() => {
        fetchInstrumentos()
    }, [selectedPais])

    const filteredInstrumentos = useMemo(
        () => filter
            ? instrumentos
                .filter(s => s[InstrumentoLCSLHPTIFields.Nombre]?.toLowerCase().includes(filter.toLowerCase()))
                .sort((a, b) => a[InstrumentoLCSLHPTIFields.Orden] - b[InstrumentoLCSLHPTIFields.Orden])
            : [...instrumentos].sort((a, b) => a[InstrumentoLCSLHPTIFields.Orden] - b[InstrumentoLCSLHPTIFields.Orden]),
        [filter, instrumentos]
    )

    const cambiarOrden = (row: InstrumentoLCSLHPTIDTO, direccion: 'up' | 'down') => {
        const rowsOrdenadas = [...instrumentos].sort((a, b) => a[InstrumentoLCSLHPTIFields.Orden] - b[InstrumentoLCSLHPTIFields.Orden])
        const indexActual = rowsOrdenadas.findIndex(item => item[EntityWithIdFields.Id] === row[EntityWithIdFields.Id])
        const indexDestino = direccion === 'up' ? indexActual - 1 : indexActual + 1
        const rowDestino = rowsOrdenadas[indexDestino]

        if (!rowDestino) {
            return
        }

        const ordenActual = row[InstrumentoLCSLHPTIFields.Orden]
        const ordenDestino = rowDestino[InstrumentoLCSLHPTIFields.Orden]
        const instrumentoSube = direccion === 'up' ? row : rowDestino
        const instrumentoBaja = direccion === 'up' ? rowDestino : row
        const tinOrdenSube = direccion === 'up' ? ordenDestino : ordenActual
        const tinOrdenBaja = direccion === 'up' ? ordenActual : ordenDestino

        toast.promise(
            HttpInstrumentoLCSLHPTI.updateOrden({
                sinCodOperacionLCSLHPInstrumentoSube: instrumentoSube[EntityWithIdFields.Id],
                tinOrdenSube,
                sinCodOperacionLCSLHPInstrumentoBaja: instrumentoBaja[EntityWithIdFields.Id],
                tinOrdenBaja
            }).then(() => fetchInstrumentos()),
            {
                success: 'Orden actualizado',
                error: 'Error al actualizar orden',
                loading: 'Actualizando orden'
            }
        )
    }

    const columnas: GridColDef<InstrumentoLCSLHPTIDTO>[] = [
        {
            headerName: '',
            field: 'ordenAcciones',
            width: 80,
            sortable: false,
            renderCell: ({ row }) => {
                const rowsOrdenadas = [...instrumentos].sort((a, b) => a[InstrumentoLCSLHPTIFields.Orden] - b[InstrumentoLCSLHPTIFields.Orden])
                const indexActual = rowsOrdenadas.findIndex(item => item[EntityWithIdFields.Id] === row[EntityWithIdFields.Id])

                return (
                    <>
                        <Tooltip title='Subir'>
                            <IconButton size='small' disabled={indexActual <= 0} onClick={() => cambiarOrden(row, 'up')}>
                                <KeyboardArrowUpIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Bajar'>
                            <IconButton
                                size='small'
                                disabled={indexActual === -1 || indexActual >= rowsOrdenadas.length - 1}
                                onClick={() => cambiarOrden(row, 'down')}
                            >
                                <KeyboardArrowDownIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </>
                )
            }
        },
        { headerName: 'Orden', field: InstrumentoLCSLHPTIFields.Orden, width: 90 },
        { headerName: 'Instrumento', field: InstrumentoLCSLHPTIFields.Nombre, flex: 1, minWidth: 240 },
        {
            headerName: 'Edita nombre',
            field: InstrumentoLCSLHPTIFields.PermiteEditarNombre,
            width: 130,
            valueFormatter: ({ value }) => value ? 'Si' : 'No'
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key={1}
                    icon={<Tooltip title='Editar'><EditIcon color='info' /></Tooltip>}
                    label='Editar'
                    showInMenu
                    onClick={() => { setSelectedEdit(row); setShowDialog(true) }}
                />,
                <GridActionsCellItem
                    key={2}
                    icon={<Tooltip title='Inactivar'><PersonRemoveIcon color='error' /></Tooltip>}
                    label='Inactivar'
                    showInMenu
                    onClick={() => askConfirmation(() => {
                        toast.promise(HttpInstrumentoLCSLHPTI.deactivate(row[EntityWithIdFields.Id]), {
                            success: 'Inactivado',
                            error: 'Error',
                            loading: 'Inactivando'
                        }).then(fetchInstrumentos)
                    })}
                />
            ]
        }
    ]

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Instrumentos LCSLHPTI'
                    action={
                        <Stack direction='row'>
                            <IconButton disabled={loading || !selectedPais} onClick={fetchInstrumentos}>
                                <CachedIcon />
                            </IconButton>
                            <Button
                                variant='contained'
                                color='primary'
                                endIcon={ICON_MAS}
                                disabled={!selectedPais}
                                onClick={() => { setSelectedEdit(undefined); setShowDialog(true) }}
                            >
                                Nuevo
                            </Button>
                        </Stack>
                    }
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Autocomplete
                        options={paisLogos}
                        value={selectedPais}
                        loading={loadingPaises}
                        onChange={(_, value) => setSelectedPais(value)}
                        getOptionLabel={option => option[EntityWithIdAndDescriptionFields.Description] ?? ''}
                        isOptionEqualToValue={(option, value) => option[PaisLogoFields.CodPais] === value[PaisLogoFields.CodPais]}
                        renderOption={(props, option) => (
                            <Box component='li' {...props}>
                                <CountryAvatar countryCode={option[PaisLogoFields.CodPais]} sx={{ mr: 1 }} />
                                {option[EntityWithIdAndDescriptionFields.Description]}
                            </Box>
                        )}
                        renderInput={params => <TextField {...params} label='Pais' size='small' />}
                        sx={{ minWidth: 240 }}
                    />
                    <TextField
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        label='Buscar'
                        size='small'
                        sx={{ minWidth: 200, flex: 1 }}
                        InputProps={{ startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                    />
                    <Tooltip title='Limpiar filtros'>
                        <IconButton size='small' onClick={() => setFilter('')}>
                            <FilterAltOff fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>
                <DataGrid
                    disableColumnFilter
                    disableColumnMenu
                    columns={columnas}
                    rows={filteredInstrumentos}
                    loading={loading}
                    density='compact'
                    hideFooterPagination
                />
            </Card>
            <Dialog open={showDialog} fullWidth maxWidth='sm' keepMounted={false}>
                {selectedPais && (
                    <EditInstrumentoLCSLHPTIForm
                        item={selectedEdit}
                        sinCodPais={selectedPais[PaisLogoFields.CodPais]}
                        onClose={() => { setShowDialog(false); setSelectedEdit(undefined); fetchInstrumentos() }}
                    />
                )}
            </Dialog>
        </WrapperTable>
    )
}

export default TableInstrumentosLCSLHPTI
